import React, { useState, useEffect } from "react";
import { useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useAuth } from "contexts/AuthContext";
import { updateProfile, changePassword } from "action/profileAction";

const ChevronRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);
const SaveIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
    <polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
  </svg>
);
const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0110 0v4"/>
  </svg>
);

function Avatar({ name, size = 64 }) {
  const initials = (name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const colors = ["#4F46E5", "#0EA5E9", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];
  const bg = colors[(name || "U").charCodeAt(0) % colors.length];
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", background: bg,
      display: "flex", alignItems: "center", justifyContent: "center",
      color: "#fff", fontSize: size * 0.33, fontWeight: 700, flexShrink: 0,
      boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
    }}>
      {initials}
    </div>
  );
}

function PasswordInput({ value, onChange, placeholder, id }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <input
        id={id}
        className="lms-input"
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{ paddingRight: 40 }}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", display: "flex", alignItems: "center" }}
      >
        {show ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  );
}

export default function MyProfile() {
  useEffect(() => { document.title = "My Profile | LMS Tenant"; }, []);

  const toast = useToast();
  const { t } = useTranslation();
  const { user, updateUser } = useAuth();

  // Profile form
  const [profileForm, setProfileForm] = useState({ name: "", email: "", phone: "", position: "" });
  const [profileErrors, setProfileErrors] = useState({});
  const [profileLoading, setProfileLoading] = useState(false);

  // Password form
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", new_password_confirmation: "" });
  const [pwErrors, setPwErrors] = useState({});
  const [pwLoading, setPwLoading] = useState(false);

  useEffect(() => {
    if (user) setProfileForm({ name: user.name || "", email: user.email || "", phone: user.phone || "", position: user.position || "" });
  }, [user]);

  const validateProfile = () => {
    const errs = {};
    if (!profileForm.name.trim()) errs.name = t("Nama wajib diisi");
    if (!profileForm.email.trim()) errs.email = t("Email wajib diisi");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileForm.email)) errs.email = t("Format email tidak valid");
    setProfileErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSaveProfile = async () => {
    if (!validateProfile()) return;
    setProfileLoading(true);
    try {
      const result = await updateProfile(profileForm);
      if (result.success) {
        updateUser({ ...user, ...result.data });
        toast({ title: t("Profil Diperbarui"), description: result.message, status: "success", duration: 3000, isClosable: true });
      } else {
        toast({ title: "Error", description: result.message || t("Gagal memperbarui profil"), status: "error", duration: 3000, isClosable: true });
      }
    } catch {
      toast({ title: "Error", description: t("Terjadi kesalahan"), status: "error", duration: 3000, isClosable: true });
    } finally {
      setProfileLoading(false);
    }
  };

  const validatePassword = () => {
    const errs = {};
    if (!pwForm.current_password) errs.current_password = t("Password saat ini wajib diisi");
    if (!pwForm.new_password) errs.new_password = t("Password baru wajib diisi");
    else if (pwForm.new_password.length < 8) errs.new_password = t("Minimal 8 karakter");
    if (!pwForm.new_password_confirmation) errs.new_password_confirmation = t("Konfirmasi password wajib diisi");
    else if (pwForm.new_password !== pwForm.new_password_confirmation) errs.new_password_confirmation = t("Password tidak cocok");
    setPwErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChangePassword = async () => {
    if (!validatePassword()) return;
    setPwLoading(true);
    try {
      const result = await changePassword(pwForm);
      if (result.success) {
        toast({ title: t("Password Diubah"), description: result.message, status: "success", duration: 3000, isClosable: true });
        setPwForm({ current_password: "", new_password: "", new_password_confirmation: "" });
        setPwErrors({});
      } else {
        toast({ title: "Error", description: result.message || t("Gagal mengubah password"), status: "error", duration: 3000, isClosable: true });
      }
    } catch {
      toast({ title: "Error", description: t("Terjadi kesalahan"), status: "error", duration: 3000, isClosable: true });
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <div className="lms-page lms-anim-fade">
      {/* Header */}
      <div className="lms-page-header">
        <div className="lms-page-header-text">
          <div className="lms-breadcrumb">
            <span>Apps</span><ChevronRightIcon /><span>Admin</span><ChevronRightIcon />
            <span className="lms-breadcrumb-current">{t("My Profile")}</span>
          </div>
          <div className="t-display">{t("My Profile")}</div>
          <div className="t-small">{t("Kelola informasi akun dan keamanan Anda.")}</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 20, alignItems: "start" }}>
        {/* Left: Avatar card */}
        <div className="lms-card" style={{ padding: 28, display: "flex", flexDirection: "column", alignItems: "center", gap: 16, textAlign: "center" }}>
          <Avatar name={user?.name} size={80} />
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "var(--text-strong)" }}>{user?.name || "—"}</div>
            <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>{user?.email || "—"}</div>
          </div>
           <div style={{ width: "100%", borderTop: "1px solid var(--border-subtle)", paddingTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
             {[
               [t("User ID"), `#${user?.id || "—"}`],
               [t("Phone"), user?.phone || "—"],
               [t("Position"), user?.position || "—"],
               [t("Status"), t("Active")],
             ].map(([label, val]) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                <span style={{ color: "var(--text-muted)" }}>{label}</span>
                <span style={{ fontWeight: 600, color: "var(--text-strong)" }}>{val}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right: forms */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Edit profile */}
          <div className="lms-card">
            <div style={{ padding: "18px 24px", borderBottom: "1px solid var(--border-subtle)", display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: "var(--brand-50)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--brand-500)" }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
              <div>
                <div className="t-h2">{t("Informasi Profil")}</div>
                <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>{t("Perbarui nama dan email akun Anda")}</div>
              </div>
            </div>
            <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
              <div className={`lms-form-group ${profileErrors.name ? "has-error" : ""}`} style={{ marginBottom: 0 }}>
                <label className="lms-label">{t("Nama Lengkap")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                <input
                  className="lms-input"
                  value={profileForm.name}
                  onChange={(e) => { setProfileForm((p) => ({ ...p, name: e.target.value })); if (profileErrors.name) setProfileErrors((p) => ({ ...p, name: "" })); }}
                  placeholder={t("Masukkan nama lengkap")}
                />
                {profileErrors.name && <div className="lms-error-msg">{profileErrors.name}</div>}
              </div>
              <div className={`lms-form-group ${profileErrors.email ? "has-error" : ""}`} style={{ marginBottom: 0 }}>
                <label className="lms-label">{t("Email")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                <input
                  className="lms-input"
                  type="email"
                  value={profileForm.email}
                  onChange={(e) => { setProfileForm((p) => ({ ...p, email: e.target.value })); if (profileErrors.email) setProfileErrors((p) => ({ ...p, email: "" })); }}
                  placeholder={t("Masukkan email")}
                />
                {profileErrors.email && <div className="lms-error-msg">{profileErrors.email}</div>}
              </div>
              <div className={`lms-form-group ${profileErrors.phone ? "has-error" : ""}`} style={{ marginBottom: 0 }}>
                <label className="lms-label">{t("No. Telepon")}</label>
                <input
                  className="lms-input"
                  value={profileForm.phone}
                  onChange={(e) => { setProfileForm((p) => ({ ...p, phone: e.target.value })); if (profileErrors.phone) setProfileErrors((p) => ({ ...p, phone: "" })); }}
                  placeholder={t("Masukkan nomor telepon")}
                />
                {profileErrors.phone && <div className="lms-error-msg">{profileErrors.phone}</div>}
              </div>
              <div className={`lms-form-group ${profileErrors.position ? "has-error" : ""}`} style={{ marginBottom: 0 }}>
                <label className="lms-label">{t("Jabatan")}</label>
                <input
                  className="lms-input"
                  value={profileForm.position}
                  onChange={(e) => { setProfileForm((p) => ({ ...p, position: e.target.value })); if (profileErrors.position) setProfileErrors((p) => ({ ...p, position: "" })); }}
                  placeholder={t("Masukkan jabatan")}
                />
                {profileErrors.position && <div className="lms-error-msg">{profileErrors.position}</div>}
              </div>
            </div>
            <div style={{ padding: "14px 24px", borderTop: "1px solid var(--border-subtle)", display: "flex", justifyContent: "flex-end" }}>
              <button className="lms-btn lms-btn-primary" onClick={handleSaveProfile} disabled={profileLoading}>
                {profileLoading ? <span className="lms-spinner" style={{ width: 14, height: 14 }} /> : <SaveIcon />}
                {profileLoading ? t("Menyimpan...") : t("Simpan Perubahan")}
              </button>
            </div>
          </div>

          {/* Change password */}
          <div className="lms-card">
            <div style={{ padding: "18px 24px", borderBottom: "1px solid var(--border-subtle)", display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: "var(--warning-50,#fffbeb)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--warning-600,#d97706)" }}>
                <LockIcon />
              </div>
              <div>
                <div className="t-h2">{t("Ubah Password")}</div>
                <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>{t("Pastikan menggunakan password yang kuat")}</div>
              </div>
            </div>
            <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
              <div className={`lms-form-group ${pwErrors.current_password ? "has-error" : ""}`} style={{ marginBottom: 0 }}>
                <label className="lms-label">{t("Password Saat Ini")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                <PasswordInput
                  id="current_password"
                  value={pwForm.current_password}
                  onChange={(e) => { setPwForm((p) => ({ ...p, current_password: e.target.value })); if (pwErrors.current_password) setPwErrors((p) => ({ ...p, current_password: "" })); }}
                  placeholder={t("Masukkan password saat ini")}
                />
                {pwErrors.current_password && <div className="lms-error-msg">{pwErrors.current_password}</div>}
              </div>
              <div className={`lms-form-group ${pwErrors.new_password ? "has-error" : ""}`} style={{ marginBottom: 0 }}>
                <label className="lms-label">{t("Password Baru")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                <PasswordInput
                  id="new_password"
                  value={pwForm.new_password}
                  onChange={(e) => { setPwForm((p) => ({ ...p, new_password: e.target.value })); if (pwErrors.new_password) setPwErrors((p) => ({ ...p, new_password: "" })); }}
                  placeholder={t("Minimal 8 karakter")}
                />
                {pwErrors.new_password && <div className="lms-error-msg">{pwErrors.new_password}</div>}
              </div>
              <div className={`lms-form-group ${pwErrors.new_password_confirmation ? "has-error" : ""}`} style={{ marginBottom: 0 }}>
                <label className="lms-label">{t("Konfirmasi Password Baru")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                <PasswordInput
                  id="new_password_confirmation"
                  value={pwForm.new_password_confirmation}
                  onChange={(e) => { setPwForm((p) => ({ ...p, new_password_confirmation: e.target.value })); if (pwErrors.new_password_confirmation) setPwErrors((p) => ({ ...p, new_password_confirmation: "" })); }}
                  placeholder={t("Ulangi password baru")}
                />
                {pwErrors.new_password_confirmation && <div className="lms-error-msg">{pwErrors.new_password_confirmation}</div>}
              </div>
            </div>
            <div style={{ padding: "14px 24px", borderTop: "1px solid var(--border-subtle)", display: "flex", justifyContent: "flex-end" }}>
              <button className="lms-btn lms-btn-primary" onClick={handleChangePassword} disabled={pwLoading}>
                {pwLoading ? <span className="lms-spinner" style={{ width: 14, height: 14 }} /> : <LockIcon />}
                {pwLoading ? t("Menyimpan...") : t("Ubah Password")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
